import { useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { appointmentState } from '../common/recoil/atom/AppointmentAtom';
import { holdCountdownState } from '../common/recoil/atom/HoldCountdownAtom';
import { getTimeDifference } from '../common/fn/Date';
import { getConfigVariable } from '../common/fn/Config';
import { useExpireSession } from './useExpireSession';

// At or under this many seconds the page shows the "will not be saved" warning.
export const WARNING_THRESHOLD_SECONDS = 120;

/**
 * Formats a remaining duration as "9 minutes 5 seconds remaining", omitting
 * zero units and handling singular/plural.
 * @param {number} minutes
 * @param {number} seconds
 * @returns {string}
 */
export const formatTimeRemaining = (minutes, seconds) => {
  const parts = [];

  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }

  if (seconds > 0 || minutes === 0) {
    parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
  }

  return `${parts.join(' ')} remaining`;
};

/**
 * Builds the 2-minute warning copy, explaining what happens if the hold
 * expires, from formatTimeRemaining's "X minutes Y seconds remaining".
 * @param {number} minutes
 * @param {number} seconds
 * @returns {string}
 */
export const getExpiryWarning = (minutes, seconds) => {
  const timeLeft = formatTimeRemaining(minutes, seconds).replace(
    / remaining$/,
    '',
  );
  return `Your appointment hold expires in ${timeLeft}. If it expires, the information you entered will not be saved and you'll need to start over.`;
};

/**
 * The one place the appointment hold is counted down: a per-second tick from
 * internal.holdDateEnd, the polite threshold announcements, and expireSession
 * ('hold') at zero. Mount it once; it mirrors the remaining time to
 * holdCountdownState (null when no hold) so other UI only has to display it.
 */
export const useHoldCountdown = () => {
  const APPOINTMENT_HOLD_DURATION = getConfigVariable(
    'APPOINTMENT_HOLD_DURATION',
  );
  const [visibleProgress, setVisibleProgress] = useState(100);
  const [minutesLeft, setMinutesLeft] = useState(APPOINTMENT_HOLD_DURATION);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const announcedThresholdRef = useRef(null);
  const selectedAppointment = useRecoilValue(appointmentState);
  const setHoldCountdown = useSetRecoilState(holdCountdownState);
  const expireSession = useExpireSession();

  useEffect(() => {
    if (typeof selectedAppointment !== 'object' || selectedAppointment === null) {
      return undefined;
    }

    // Reset the announcement tracking whenever a new hold begins.
    announcedThresholdRef.current = null;

    let timer = null;

    const updateTimer = () => {
      clearTimeout(timer);
      const { holdDateEnd } = selectedAppointment.internal;

      const { minutes, seconds, percent } = getTimeDifference(
        holdDateEnd,
        APPOINTMENT_HOLD_DURATION,
      );

      setMinutesLeft(minutes);
      setSecondsLeft(seconds);
      setVisibleProgress(percent);

      const totalSecondsLeft = Math.max(minutes * 60 + seconds, 0);

      // Only update the polite status region at meaningful thresholds (hold
      // start, 5 min, 2 min, 1 min, 30s) so it isn't announced every second.
      let threshold = null;
      if (totalSecondsLeft <= 0) {
        threshold = 0;
      } else if (totalSecondsLeft <= 30) {
        threshold = 30;
      } else if (totalSecondsLeft <= 60) {
        threshold = 60;
      } else if (totalSecondsLeft <= WARNING_THRESHOLD_SECONDS) {
        threshold = 120;
      } else if (totalSecondsLeft <= 300) {
        threshold = 300;
      } else {
        threshold = 'start';
      }

      if (threshold !== announcedThresholdRef.current) {
        announcedThresholdRef.current = threshold;

        if (threshold === 0) {
          setStatusMessage('Your appointment hold has expired.');
        } else if (threshold === 120) {
          setStatusMessage(getExpiryWarning(minutes, seconds));
        } else {
          setStatusMessage(
            `Appointment hold: ${formatTimeRemaining(minutes, seconds)}.`,
          );
        }
      }

      if (percent === 0) {
        setHoldCountdown(null);
        expireSession('hold');
      } else {
        setHoldCountdown({ minutes, seconds });
        timer = setTimeout(() => {
          updateTimer();
        }, 1000);
      }
    };

    updateTimer();

    return () => {
      clearTimeout(timer);
      setHoldCountdown(null);
    };
  }, [selectedAppointment, APPOINTMENT_HOLD_DURATION, expireSession, setHoldCountdown]);

  return { minutesLeft, secondsLeft, visibleProgress, statusMessage };
};
